import { addDays, dayOfWeek } from './leave-entitlement-policy';
import { shiftCalendarMonths } from './nurses-personal-leave-basis';

export type RosterPresence = 'YES' | 'UNKNOWN' | 'NO';
export interface NursesRosterFact {
  sourceRef: string;
  /** One source shift, split in SQL at tenant-local midnight and 07:00/19:00. */
  intervals: readonly { date: string; minutes: number; outside: boolean }[];
  /** Tenant-local weekday/start/end geometry, supplied by the roster reader. */
  rosterPatternKey?: string;
  totalMinutes: number;
  recordedBreakMinutes: number;
  /** Only governed unpaid-break evidence can supply this value. */
  establishedUnpaidBreakMinutes: number | null;
  /** null means unestablished. A false whole-shift is_overtime is NOT evidence. */
  establishedOvertimeMinutes: number | null;
  ordinaryEvidenceRef: string | null;
  ordinaryEvidenceBasis?: 'LINKED_APPROVED_ATTENDANCE_EXACT_ROSTER_WINDOW';
  historicalVersionComplete: boolean;
  reliefOnly: boolean;
}
export interface NursesEngagementSegment {
  start: string;
  endExclusive: string;
  engagementStart?: string;
  ordinaryWeeklyHours1e4: number;
  employment: string;
  award: string;
  employeeCategory: string | null;
  status: string;
  evidenceRefs: string[];
  /** Employment boundaries are separate from the roster qualification screen. */
  boundariesComplete: boolean;
}
export interface NursesAccrualDay {
  date: string;
  ordinaryUnits1e4: number;
  /** Full non-accrual day only; partial unpaid/WC absence without a fraction is incomplete. */
  accrualBearing: boolean;
  sourceRefs: string[];
}
export interface NursesMonthlyEvidence {
  segments: readonly NursesEngagementSegment[];
  roster: readonly NursesRosterFact[];
  coverage: readonly { start: string; endExclusive: string; sourceRef: string }[];
  accrualDays: readonly NursesAccrualDay[];
  engagementComplete: boolean;
  absenceComplete: boolean;
  paidAbsencePeriods?: readonly { start: string; endExclusive: string; sourceRef: string }[];
  reviewReasons: readonly string[];
}

const integer = (n: number) => Number.isSafeInteger(n) && n >= 0;
export function nursesOutsideSpanBounds(input: {
  totalMinutes: number;
  outsideMinutes: number;
  recordedBreakMinutes?: number;
  establishedUnpaidBreakMinutes: number | null;
  establishedOvertimeMinutes: number | null;
  ordinaryEvidenceRef: string | null;
}) {
  const T = input.totalMinutes;
  const X = input.outsideMinutes;
  const B = input.establishedUnpaidBreakMinutes ?? 0;
  const O = input.establishedOvertimeMinutes;
  const valid =
    [T, X, B].every(integer) && X <= T && B <= T && (O === null || (integer(O) && B + O <= T));
  const ordinaryEstablished = O !== null && Boolean(input.ordinaryEvidenceRef?.trim());
  const lower = valid && ordinaryEstablished ? Math.max(0, X - B - O!) : 0;
  const upper = valid ? Math.min(X, T - B - (ordinaryEstablished ? O! : 0)) : null;
  const presence: RosterPresence = !valid
    ? 'UNKNOWN'
    : lower > 0
      ? 'YES'
      : upper === 0
        ? 'NO'
        : 'UNKNOWN';
  return {
    presence,
    lower,
    upper,
    valid,
    ordinaryEstablished,
    breakAssumption:
      input.establishedUnpaidBreakMinutes === null
        ? 'PAID_BREAK_DEFAULT_CL_22_13'
        : 'ESTABLISHED_UNPAID_BREAK',
  };
}
const monday = (date: string) => addDays(date, -((dayOfWeek(date) + 6) % 7));

/** ADOPTED_INTERPRETATION: Paul, 2 October 2026. A positive screen, never an eligibility denial. */
export function nursesRosterScreen(evidence: NursesMonthlyEvidence, asOf: string, start: string) {
  const endExclusive = monday(asOf); // Only completed local Monday–Sunday weeks.
  const from = [addDays(endExclusive, -91), start].sort().at(-1)!;
  const weeks = new Map<string, RosterPresence>();
  const reasons = new Set<string>();
  const bounds: Array<{
    week: string;
    sourceRef: string;
    lower: number;
    upper: number | null;
    presence: RosterPresence;
    ordinaryEvidenceRef: string | null;
    ordinaryEvidenceBasis: string | null;
    breakAssumption: string;
  }> = [];
  for (const fact of evidence.roster) {
    const allMinutes = fact.intervals.reduce((sum, i) => sum + i.minutes, 0);
    const shapeValid =
      allMinutes === fact.totalMinutes && fact.intervals.every((i) => integer(i.minutes));
    if (!shapeValid) reasons.add('NURSES_ROSTER_INPUT_INVALID');
    if (!fact.historicalVersionComplete) reasons.add('NURSES_ROSTER_HISTORICAL_VERSION_INCOMPLETE');
    if (fact.reliefOnly) reasons.add('NURSES_CL_22_12_RELIEF_ONLY_REVIEW');
    if (fact.establishedUnpaidBreakMinutes === null && fact.recordedBreakMinutes > 0)
      reasons.add('NURSES_PAID_BREAK_DEFAULT_ASSUMPTION');
    const byWeek = new Map<string, { minutes: number; outside: number }>();
    for (const i of fact.intervals.filter((i) => i.date >= from && i.date < endExclusive)) {
      const key = monday(i.date);
      const group = byWeek.get(key) ?? { minutes: 0, outside: 0 };
      group.minutes += i.minutes;
      group.outside += i.outside ? i.minutes : 0;
      byWeek.set(key, group);
    }
    for (const [key, group] of byWeek) {
      // Global shift deductions bound the subset too: never place a break/OT in an invented interval.
      const bound = nursesOutsideSpanBounds({ ...fact, outsideMinutes: group.outside });
      if (!bound.valid) reasons.add('NURSES_ROSTER_INPUT_INVALID');
      if (
        bound.ordinaryEstablished &&
        fact.totalMinutes -
          (fact.establishedUnpaidBreakMinutes ?? 0) -
          fact.establishedOvertimeMinutes! ===
          0
      )
        continue;
      const presence = shapeValid && fact.historicalVersionComplete ? bound.presence : 'UNKNOWN';
      bounds.push({
        week: key,
        sourceRef: fact.sourceRef,
        lower: bound.lower,
        upper: bound.upper,
        presence,
        ordinaryEvidenceRef: fact.ordinaryEvidenceRef,
        ordinaryEvidenceBasis: fact.ordinaryEvidenceBasis ?? null,
        breakAssumption: bound.breakAssumption,
      });
      const previous = weeks.get(key);
      weeks.set(
        key,
        previous === 'YES' || presence === 'YES'
          ? 'YES'
          : previous === 'UNKNOWN' || presence === 'UNKNOWN'
            ? 'UNKNOWN'
            : 'NO',
      );
    }
  }
  let coverageComplete = true;
  for (let date = from; date < endExclusive; date = addDays(date, 1))
    if (!evidence.coverage.some((c) => c.start <= date && date < c.endExclusive))
      coverageComplete = false;
  const W = weeks.size;
  const Y = [...weeks.values()].filter((v) => v === 'YES').length;
  const U = [...weeks.values()].filter((v) => v === 'UNKNOWN').length;
  const supported = coverageComplete && W >= 4 && 2 * Y >= W && evidence.engagementComplete;
  // Integer comparisons at 50%; UNKNOWN always remains in W.
  const flippingChanges = W >= 4 && (2 * Y >= W ? 2 * (Y - 1) < W : 2 * (Y + 1) >= W);
  if (flippingChanges) reasons.add('NURSES_REGULARITY_ONE_WEEK_SENSITIVE');
  if (2 * Y < W && 2 * (Y + U) >= W && U > 0)
    reasons.add('NURSES_REGULARITY_UNCERTAINTY_CROSSES_HALF');
  if (W < 4) reasons.add('NURSES_INSUFFICIENT_ROSTER_WEEKS');
  if (!coverageComplete) reasons.add('NURSES_ROSTER_COVERAGE_INCOMPLETE');
  if (!supported) reasons.add('NURSES_REGULARITY_NOT_ESTABLISHED_BY_SCREEN_REVIEW');
  return {
    status: supported ? ('SUPPORTED' as const) : ('REVIEW' as const),
    label: supported ? 'regularity supported by roster screen' : 'not established by screen',
    interpretation: 'ADOPTED_INTERPRETATION',
    adoptedBy: 'Paul',
    adoptedOn: '2026-10-02',
    from,
    endExclusive,
    W,
    Y,
    U,
    coverageComplete,
    reasons: [...reasons].sort(),
    weeks: [...weeks]
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([week, presence]) => ({ week, presence })),
    bounds,
  };
}

/** Paid absence preserves a supported roster only within the same known segment.
 * On return, compare every dated shift with the last completed supported week;
 * missing geometry/ordinary evidence or any changed pattern cannot be bridged.
 */
function paidAbsenceCarryComplete(
  evidence: NursesMonthlyEvidence,
  segment: NursesEngagementSegment,
  periodStart: string,
  through: string,
) {
  if (!segment.boundariesComplete || !segment.evidenceRefs.length || periodStart < segment.start)
    return false;
  const referenceEnd = monday(periodStart);
  const referenceStart = addDays(referenceEnd, -7);
  const signature = (date: string): string[] | null => {
    const facts = evidence.roster.filter((r) => r.intervals.some((i) => i.date === date));
    if (
      facts.some(
        (r) =>
          !r.rosterPatternKey ||
          !r.historicalVersionComplete ||
          r.reliefOnly ||
          r.establishedOvertimeMinutes === null ||
          !r.ordinaryEvidenceRef?.trim() ||
          r.intervals.reduce((n, i) => n + i.minutes, 0) !== r.totalMinutes ||
          !nursesOutsideSpanBounds({
            ...r,
            outsideMinutes: r.intervals.filter((i) => i.outside).reduce((n, i) => n + i.minutes, 0),
          }).valid,
      )
    )
      return null;
    return facts
      .map((r) =>
        JSON.stringify([
          r.rosterPatternKey,
          r.totalMinutes,
          r.establishedUnpaidBreakMinutes,
          r.establishedOvertimeMinutes,
          r.intervals.map((i) => [dayOfWeek(i.date), i.minutes, i.outside]),
        ]),
      )
      .sort();
  };
  let returned = false;
  for (let date = periodStart; date <= through; date = addDays(date, 1)) {
    if (!evidence.coverage.some((c) => c.start <= date && date < c.endExclusive)) return false;
    if (evidence.paidAbsencePeriods?.some((p) => p.start <= date && date < p.endExclusive))
      continue;
    const expected = signature(addDays(referenceStart, (dayOfWeek(date) + 6) % 7));
    const actual = signature(date);
    if (!expected || !actual || JSON.stringify(expected) !== JSON.stringify(actual)) return false;
    // A gap before the first evidenced return cannot be treated as more paid leave.
    if (!returned && actual.length === 0) return false;
    returned = true;
  }
  return true;
}

const roundRatio = (n: bigint, d: bigint) => Number((n * 2n + d) / (2n * d));
/** Calendar-month sums use rational integer arithmetic, rounding only once per month. */
export function nursesMonthlyEntitlement(
  evidence: NursesMonthlyEvidence,
  startDate: string,
  asOf: string,
  scopeStart: string,
  scopeEndExclusive: string,
) {
  const reasons = new Set(evidence.reviewReasons);
  if (!evidence.engagementComplete) reasons.add('NURSES_ENGAGEMENT_BOUNDARIES_INCOMPLETE');
  if (!evidence.absenceComplete) reasons.add('NURSES_ACCRUAL_ABSENCE_EVIDENCE_INCOMPLETE');
  const screens = evidence.segments.flatMap((s) => {
    const result = [];
    for (
      let month = [s.start, startDate].sort().at(-1)!.slice(0, 7) + '-01', count = 0;
      month <= asOf && month < s.endExclusive && count < 1200;
      month = shiftCalendarMonths(month, 1), count++
    ) {
      const through = [
        asOf,
        addDays(s.endExclusive, -1),
        addDays(shiftCalendarMonths(month, 1), -1),
      ].sort()[0]!;
      let screen = nursesRosterScreen(evidence, through, s.engagementStart ?? s.start);
      let carriedFrom: string | null = null;
      if (
        screen.status !== 'SUPPORTED' &&
        evidence.engagementComplete &&
        evidence.absenceComplete
      ) {
        for (const period of [...(evidence.paidAbsencePeriods ?? [])].sort((a, b) =>
          b.start.localeCompare(a.start),
        )) {
          if (period.start > through) continue;
          if (!paidAbsenceCarryComplete(evidence, s, period.start, through)) continue;
          const prior = nursesRosterScreen(evidence, period.start, s.engagementStart ?? s.start);
          if (prior.status === 'SUPPORTED') {
            screen = prior;
            carriedFrom = period.start;
            break;
          }
        }
      }
      result.push({ segment: s, month: month.slice(0, 7), screen, carriedFrom });
    }
    return result;
  });
  for (const { segment, screen } of screens) {
    if (
      !segment.boundariesComplete ||
      !segment.evidenceRefs.length ||
      !integer(segment.ordinaryWeeklyHours1e4) ||
      segment.ordinaryWeeklyHours1e4 > 380000 ||
      segment.endExclusive <= segment.start ||
      segment.award !== 'MA000034' ||
      !['FULL_TIME', 'PART_TIME', 'PERMANENT_FT', 'PERMANENT_PT'].includes(segment.employment)
    )
      reasons.add('NURSES_ENGAGEMENT_SEGMENT_INVALID');
    screen.reasons.forEach((r) => reasons.add(r));
  }
  const ordered = [...evidence.segments].sort((a, b) => a.start.localeCompare(b.start));
  if (ordered.some((s, i) => i > 0 && s.start < ordered[i - 1]!.endExclusive))
    reasons.add('NURSES_ENGAGEMENT_OVERLAP');
  // A complete dated history supplies the exclusive employment/engagement end.
  // Reads may also clip it to asOf + 1; either way, later calendar time is not service.
  const serviceEndExclusive = ordered
    .map((s) => s.endExclusive)
    .sort()
    .at(-1);
  const serviceThrough =
    evidence.engagementComplete && serviceEndExclusive
      ? [asOf, addDays(serviceEndExclusive, -1)].sort()[0]!
      : asOf;
  const dayMap = new Map(evidence.accrualDays.map((d) => [d.date, d]));
  if (
    dayMap.size !== evidence.accrualDays.length ||
    evidence.accrualDays.some(
      (d) =>
        !integer(d.ordinaryUnits1e4) ||
        !d.sourceRefs.length ||
        (!d.accrualBearing && d.ordinaryUnits1e4 > 0),
    )
  )
    reasons.add('NURSES_ACCRUAL_DAYS_INVALID');
  const months = new Map<
    string,
    { numerator: bigint; before: bigint; through: bigint; days: number }
  >();
  let ordinaryBefore = 0n,
    ordinaryThrough = 0n;
  for (
    let date = startDate, count = 0;
    date <= serviceThrough && count < 36600;
    date = addDays(date, 1), count++
  ) {
    const applicable = screens.filter(
      ({ segment: s, month }) =>
        month === date.slice(0, 7) && s.start <= date && date < s.endExclusive,
    );
    if (!applicable.length) {
      reasons.add('NURSES_ENGAGEMENT_COVERAGE_INCOMPLETE');
      continue;
    }
    if (applicable.length !== 1) continue;
    const { segment, screen } = applicable[0]!;
    if (screen.status !== 'SUPPORTED' || !integer(segment.ordinaryWeeklyHours1e4)) continue;
    const fact = dayMap.get(date);
    if (fact && !fact.accrualBearing) continue;
    const key = date.slice(0, 7);
    const days = Number(addDays(shiftCalendarMonths(key + '-01', 1), -1).slice(8, 10));
    const m = months.get(key) ?? { numerator: 0n, before: 0n, through: 0n, days };
    const weekly = BigInt(segment.ordinaryWeeklyHours1e4);
    m.numerator += weekly;
    if (date < scopeStart) m.before += weekly;
    if (date < scopeEndExclusive) m.through += weekly;
    months.set(key, m);
    if (fact && integer(fact.ordinaryUnits1e4)) {
      if (date < scopeStart) ordinaryBefore += BigInt(fact.ordinaryUnits1e4);
      if (date < scopeEndExclusive) ordinaryThrough += BigInt(fact.ordinaryUnits1e4);
    }
  }
  const serviceProvisoDate = shiftCalendarMonths(startDate, 12);
  let continuousServiceEnd = startDate;
  for (const segment of [...ordered].sort((a, b) =>
    (a.engagementStart ?? a.start).localeCompare(b.engagementStart ?? b.start),
  )) {
    if (
      !segment.boundariesComplete ||
      (segment.engagementStart ?? segment.start) > continuousServiceEnd
    )
      break;
    if (segment.endExclusive > continuousServiceEnd) continuousServiceEnd = segment.endExclusive;
  }
  const serviceProvisoMet =
    evidence.engagementComplete &&
    serviceThrough >= serviceProvisoDate &&
    continuousServiceEnd > serviceProvisoDate;
  const monthly = [...months].map(([month, m]) => ({
    month,
    units1e4: roundRatio(m.numerator, BigInt(10 * m.days)),
    scopeUnits1e4:
      roundRatio(m.through, BigInt(10 * m.days)) - roundRatio(m.before, BigInt(10 * m.days)),
    serviceProvisoMet,
    retroactiveCredit: serviceProvisoMet && month < serviceProvisoDate.slice(0, 7),
  }));
  const eaBefore = [...months.values()].reduce(
    (sum, m) => sum + roundRatio(m.before, BigInt(10 * m.days)),
    0,
  );
  const eaThrough = [...months.values()].reduce(
    (sum, m) => sum + roundRatio(m.through, BigInt(10 * m.days)),
    0,
  );
  const nesBefore = roundRatio(ordinaryBefore, 52n),
    nesThrough = roundRatio(ordinaryThrough, 52n);
  const cumulativeBefore = Math.max(nesBefore, serviceProvisoMet ? eaBefore : 0);
  const cumulativeThrough = Math.max(nesThrough, serviceProvisoMet ? eaThrough : 0);
  const blockingReasons = [...reasons].filter((r) => r !== 'NURSES_PAID_BREAK_DEFAULT_ASSUMPTION');
  return {
    expectedUnits1e4: cumulativeThrough - cumulativeBefore,
    months: monthly,
    nesFloorUnits1e4: nesThrough,
    payableEAUnits1e4: serviceProvisoMet ? eaThrough : 0,
    cumulativeBeforeUnits1e4: cumulativeBefore,
    cumulativeThroughUnits1e4: cumulativeThrough,
    serviceProvisoDate,
    serviceProvisoMet,
    screens: screens.map(({ segment, month, screen, carriedFrom }) => ({
      month,
      start: segment.start,
      endExclusive: segment.endExclusive,
      evidenceRefs: segment.evidenceRefs,
      screen,
      carriedThroughPaidLeaveFrom: carriedFrom,
    })),
    complete: blockingReasons.length === 0 && screens.length > 0,
    reviewReasons: [...reasons].sort(),
    interpretation: 'ADOPTED_INTERPRETATION',
    adoptedBy: 'Paul',
    adoptedOn: '2026-10-02',
    providerConfirmation: 'advisable',
  };
}

const NURSES_REVIEW_TEXT: Readonly<Record<string, string>> = {
  NURSES_ACCRUAL_WORK_HISTORY_INCOMPLETE:
    'A final historical roster interval lacks linked approved ordinary work or a complete accrual-bearing paid absence.',
  NURSES_ROSTER_INPUT_INVALID:
    'A roster interval has invalid or incomplete integer-minute geometry.',
  NURSES_ROSTER_EVIDENCE_MISSING: 'Published historical roster evidence is missing.',
  NURSES_ENGAGEMENT_BOUNDARIES_INCOMPLETE:
    'Continuous shiftworker engagement boundaries are not established.',
  NURSES_ACCRUAL_ABSENCE_EVIDENCE_INCOMPLETE:
    'Dated accrual-bearing absence evidence is incomplete.',
  NURSES_REGULARITY_NOT_ESTABLISHED_BY_SCREEN_REVIEW:
    'Regularity is not established by the adopted roster screen; a documented determination is required.',
  NURSES_REGULARITY_ONE_WEEK_SENSITIVE:
    'One roster week changing classification changes the screening result.',
  NURSES_REGULARITY_UNCERTAINTY_CROSSES_HALF:
    'Uncertain roster weeks cross the screening threshold.',
  NURSES_INSUFFICIENT_ROSTER_WEEKS:
    'Fewer than four ordinary roster weeks are available for the screen.',
  NURSES_ROSTER_COVERAGE_INCOMPLETE: 'Published roster coverage is incomplete.',
  NURSES_ROSTER_HISTORICAL_VERSION_INCOMPLETE: 'A historical roster version is unresolved.',
  NURSES_ROSTER_HISTORICAL_VERSION_REVIEW:
    'Historical roster variations require a documented determination.',
  NURSES_CL_22_12_RELIEF_ONLY_REVIEW:
    'Relief-only status under clause 22.12 requires review of independent roster qualification.',
  NURSES_CL_22_12_RELIEF_STATUS_DETERMINATION_REQUIRED:
    'The engagement category does not establish whether clause 22.12 relief status applies.',
  NURSES_ATTENDANCE_ONLY_DETERMINATION_REQUIRED:
    'Attendance without roster or approved-variation linkage requires a documented determination.',
  NURSES_PAID_BREAK_DEFAULT_ASSUMPTION:
    'Recorded nurse breaks are treated as paid under clause 22.13 unless governed evidence establishes unpaid treatment.',
  NURSES_ENGAGEMENT_SEGMENT_INVALID:
    'A dated engagement segment lacks valid hours, classification or boundary evidence.',
  NURSES_ENGAGEMENT_OVERLAP: 'Dated engagement segments overlap.',
  NURSES_ENGAGEMENT_COVERAGE_INCOMPLETE:
    'Dated engagement evidence does not cover the calculation period.',
  NURSES_ACCRUAL_DAYS_INVALID: 'Dated ordinary accrual quantities are invalid or duplicated.',
  NURSES_S130_ABSENCE_BOUNDARIES_INCOMPLETE:
    'Workers compensation absence boundaries under section 130 are not established.',
  NURSES_UNPAID_ABSENCE_BOUNDARIES_INCOMPLETE:
    'Full or partial unpaid absence boundaries are not established.',
  NURSES_LEAVE_APPROVAL_OR_QUANTITY_GRAPH_INCOMPLETE:
    'A leave request lacks coherent canonical approval, version or dated quantities.',
};
export function nursesReviewReasonText(reasons: readonly string[]) {
  return reasons
    .map(
      (r) =>
        NURSES_REVIEW_TEXT[r] ?? 'Nurse entitlement evidence requires a documented determination.',
    )
    .join(' ');
}
